import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';

interface SlidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
}

export const SlidePanel: React.FC<SlidePanelProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = 480,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (typeof document === 'undefined') return null;

  // Portal into document.body so the panel escapes any ancestor's
  // positioning / transform / overflow context and reliably anchors
  // to the viewport's right edge from top to bottom.
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/35 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 bg-white z-50 flex flex-col shadow-[-8px_0_32px_rgba(0,0,0,0.18)]"
            style={{ width: `${width}px` }}
          >
            <header className="h-16 border-b border-[#F1F5F9] px-6 flex items-center justify-between flex-shrink-0 bg-white">
              <div className="flex flex-col">
                <h3 className="text-[16px] font-semibold text-[#0F172A]">{title}</h3>
                {subtitle && <p className="text-[12px] text-[#94A3B8]">{subtitle}</p>}
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full bg-[#F1F5F9] flex items-center justify-center text-[#374151] hover:bg-[#E2E8F0] transition-colors"
              >
                <X size={18} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto p-6 custom-scrollbar min-h-0">
              {children}
            </div>

            {footer && (
              <footer className="h-[72px] border-t border-[#F1F5F9] px-6 flex items-center justify-end gap-3 flex-shrink-0 bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
                {footer}
              </footer>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};
